import { useMemo, useState } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import {
  FileSpreadsheet,
  PackageCheck,
  Plus,
  Trash2,
} from "lucide-react";
import {
  Link,
  useNavigate,
} from "react-router-dom";
import {
  Card,
  EmptyState,
  InlineNotice,
  Money,
  PageHeader,
  QuantityControl,
} from "../components/ui";
import {
  db,
  type CachedProduct,
  type CachedProductUnit,
} from "../lib/db";
import {
  applyLocalReceiveStock,
  type LocalReceiveLine,
} from "../lib/local-operations";
import { flushSyncQueue } from "../lib/sync";
import { useAuth } from "../lib/auth";
import { api, ApiError } from "../lib/api";
import { refreshFromServer } from "../lib/data";

interface Line extends LocalReceiveLine {
  id: string;
  lotCode: string;
  expiryDate: string;
}

export function ReceiveStockPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const products = useLiveQuery(
    () => db.products.orderBy("name").toArray(),
    [],
    [],
  );
  const suppliers = useLiveQuery(
    () => db.suppliers.orderBy("name").toArray(),
    [],
    [],
  );

  const [lines, setLines] = useState<Line[]>([]);
  const [supplierId, setSupplierId] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<
    "CASH" | "BANK_TRANSFER" | "DEBT"
  >("CASH");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const bankPaymentAvailable = auth.demo || navigator.onLine;

  const total = useMemo(
    () =>
      lines.reduce(
        (sum, line) =>
          sum + Math.round(line.quantity * line.unitCost),
        0,
      ),
    [lines],
  );

  function add(product: CachedProduct) {
    if (lines.some((line) => line.product.id === product.id)) {
      return;
    }

    setLines([
      ...lines,
      {
        id: crypto.randomUUID(),
        product,
        unit: null,
        quantity: 1,
        multiplier: 1,
        unitCost: 0,
        lotCode: "",
        expiryDate: "",
      },
    ]);
  }

  function patch(id: string, patchValue: Partial<Line>) {
    setLines((current) =>
      current.map((line) =>
        line.id === id ? { ...line, ...patchValue } : line,
      ),
    );
  }

  function selectUnit(line: Line, unitId: string) {
    const unit: CachedProductUnit | null =
      unitId === "base"
        ? null
        : line.product.units.find(
            (candidate) => candidate.id === unitId,
          ) ?? null;

    const multiplier = unit?.multiplier ?? 1;
    patch(line.id, {
      unit,
      multiplier,
      unitCost: 0,
    });
  }

  async function submit() {
    setError("");

    if (lines.length === 0) return;

    if (paymentMethod === "BANK_TRANSFER" && !bankPaymentAvailable) {
      setError("Chuyển khoản cần kết nối mạng để xác nhận.");
      return;
    }

    if (paymentMethod === "DEBT" && !supplierId) {
      setError("Ghi nợ cần chọn nhà cung cấp.");
      return;
    }

    const missingExpiry = lines.find(
      (line) => line.product.trackExpiry && !line.expiryDate,
    );
    if (missingExpiry) {
      setError(`${missingExpiry.product.name} cần nhập hạn sử dụng.`);
      return;
    }

    if (lines.some((line) => line.quantity <= 0 || line.unitCost <= 0)) {
      setError(
        "Số lượng và giá nhập phải lớn hơn 0. Hàng biếu/tặng hoặc điều chỉnh số dư nên dùng nghiệp vụ Điều chỉnh tồn.",
      );
      return;
    }

    setBusy(true);
    const idempotencyKey = crypto.randomUUID();

    const payload = {
      idempotencyKey,
      supplierId: supplierId || null,
      lines: lines.map((line) => ({
        productId: line.product.id,
        unitId: line.unit?.id ?? null,
        quantity: line.quantity,
        unitCost: Math.round(line.unitCost),
        lotCode: line.lotCode.trim() || null,
        expiryDate: line.expiryDate || null,
      })),
      paymentMethod,
    };

    try {
      if (auth.demo || !navigator.onLine) {
        await applyLocalReceiveStock(
          lines,
          paymentMethod,
          idempotencyKey,
          supplierId || null,
          {
            demo: auth.demo,
            tenantId: auth.session?.tenant?.id,
            storeId: auth.session?.store?.id,
          },
        );
      } else {
        try {
          await api("/inventory/receive", {
            method: "POST",
            body: JSON.stringify(payload),
          });
          await refreshFromServer();
        } catch (onlineError) {
          if (onlineError instanceof ApiError && onlineError.status < 500) {
            throw onlineError;
          }

          await applyLocalReceiveStock(
            lines,
            paymentMethod,
            idempotencyKey,
            supplierId || null,
            {
              demo: false,
              tenantId: auth.session?.tenant?.id,
              storeId: auth.session?.store?.id,
            },
          );

          const retry = await flushSyncQueue(
            auth.session?.tenant?.id,
            auth.session?.store?.id,
          );
          if (retry.failed === 0 && retry.pendingRemaining === 0) {
            await refreshFromServer();
          }
        }
      }

      navigate("/inventory", { replace: true });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Không thể hoàn tất nhập hàng",
      );
    } finally {
      setBusy(false);
    }
  }

  const debtNeedsSupplier = paymentMethod === "DEBT" && !supplierId;

  return (
    <div className="page-stack">
      <PageHeader
        title="Nhập hàng"
        description="Nhập theo thùng/lốc/chai nhưng tồn được quy về đơn vị cơ bản"
        back
        action={
          <Link className="btn btn-secondary" to="/inventory/import">
            <FileSpreadsheet size={18} /> Nhập từ tệp / Hóa đơn
          </Link>
        }
      />

      {error ? <InlineNotice tone="danger" title={error} /> : null}

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "#f0fdf4",
          border: "1px solid #bbf7d0",
          borderRadius: "12px",
          padding: "14px 18px",
          gap: "12px",
          flexWrap: "wrap",
        }}
      >
        <div>
          <strong style={{ color: "#166534" }}>
            Cần nhập hàng loạt từ Excel / CSV hoặc Chụp phiếu nhập?
          </strong>
          <div style={{ color: "#15803d", fontSize: "0.875rem", marginTop: "2px" }}>
            Sử dụng quy trình Nhập liệu thông minh: Đọc tệp → Khớp cột → Bảng nháp kiểm tra an toàn → Xác nhận nhập kho chuẩn sổ cái.
          </div>
        </div>
        <Link className="btn btn-primary compact" to="/inventory/import">
          <FileSpreadsheet size={16} /> Mở Nhập liệu thông minh
        </Link>
      </div>

      <Card className="form-stack">
        <label>
          Nhà cung cấp
          <select
            value={supplierId}
            onChange={(event) => setSupplierId(event.target.value)}
          >
            <option value="">Không chọn</option>
            {suppliers.map((supplier) => (
              <option key={supplier.id} value={supplier.id}>
                {supplier.name}
              </option>
            ))}
          </select>
        </label>

        {suppliers.length === 0 ? (
          <InlineNotice tone="info" title="Chưa có nhà cung cấp">
            <span>
              Tạo trong{" "}
              <Link to="/partners">
                <strong>Khách hàng & nhà cung cấp</strong>
              </Link>{" "}
              nếu muốn ghi nợ.
            </span>
          </InlineNotice>
        ) : null}
      </Card>

      <Card>
        <h2>Chọn sản phẩm</h2>
        {products.length === 0 ? (
          <InlineNotice tone="warning" title="Chưa có SKU trong Danh mục sản phẩm">
            <span>
              Nhập hàng chỉ áp dụng cho SKU đã tồn tại. Hãy tạo mặt hàng tại{" "}
              <Link to="/products/new">
                <strong>Sản phẩm → Thêm sản phẩm</strong>
              </Link>{" "}
              trước khi lập phiếu nhập.
            </span>
          </InlineNotice>
        ) : null}
        <div className="receive-product-picker">
          {products.map((product) => (
            <button
              key={product.id}
              onClick={() => add(product)}
              disabled={lines.some((line) => line.product.id === product.id)}
            >
              <Plus size={17} /> {product.name}
            </button>
          ))}
        </div>
      </Card>

      {lines.length === 0 ? (
        <EmptyState
          title="Chưa có mặt hàng nhập"
          description="Chọn ít nhất một sản phẩm phía trên hoặc dùng Nhập thông minh nếu nhập danh sách dài."
        />
      ) : (
        <Card>
          <div className="receive-lines">
            {lines.map((line) => (
              <div className="receive-line" key={line.id}>
                <div className="receive-line-title">
                  <strong>{line.product.name}</strong>
                  <button
                    className="icon-button danger"
                    onClick={() =>
                      setLines(lines.filter((item) => item.id !== line.id))
                    }
                    aria-label="Xóa dòng nhập"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>

                <div className="form-grid">
                  <label>
                    Quy cách nhập
                    <select
                      value={line.unit?.id ?? "base"}
                      onChange={(event) =>
                        selectUnit(line, event.target.value)
                      }
                    >
                      <option value="base">{line.product.baseUnit}</option>
                      {line.product.units.map((unit) => (
                        <option key={unit.id} value={unit.id}>
                          {unit.name} (1 = {unit.multiplier}{" "}
                          {line.product.baseUnit})
                        </option>
                      ))}
                    </select>
                  </label>

                  <label>
                    Số lượng ({line.unit?.name ?? line.product.baseUnit})
                    <div style={{ marginTop: 4 }}>
                      <QuantityControl
                        value={line.quantity}
                        min={0.001}
                        step={1}
                        allowDecimal={true}
                        unitName={line.unit?.name ?? line.product.baseUnit}
                        onChange={(qty) => patch(line.id, { quantity: qty })}
                      />
                    </div>
                  </label>

                  <label>
                    Giá nhập / {line.unit?.name ?? line.product.baseUnit}
                    <input
                      type="number"
                      min="1"
                      step="100"
                      value={line.unitCost}
                      onChange={(event) =>
                        patch(line.id, {
                          unitCost: Number(event.target.value),
                        })
                      }
                    />
                    <small>
                      Không tự lấy giá vốn cũ làm giá nhập mới. Giá vốn hiện tại chỉ tham khảo:{" "}
                      {line.product.avgCost.toLocaleString("vi-VN")} ₫ / {line.product.baseUnit}
                      {line.unit
                        ? ` (≈ ${Math.round(line.product.avgCost * line.multiplier).toLocaleString("vi-VN")} ₫ / ${line.unit.name})`
                        : ""}
                    </small>
                  </label>

                  <div className="conversion-preview">
                    <span>Quy đổi tồn</span>
                    <strong>
                      {Math.round(line.quantity * line.multiplier * 1000) / 1000}{" "}
                      {line.product.baseUnit}
                    </strong>
                  </div>
                </div>

                {line.product.trackExpiry ? (
                  <div className="form-grid">
                    <label>
                      Mã lô
                      <input
                        value={line.lotCode}
                        onChange={(event) =>
                          patch(line.id, {
                            lotCode: event.target.value,
                          })
                        }
                        placeholder="Không bắt buộc"
                      />
                    </label>

                    <label>
                      Hạn sử dụng
                      <input
                        type="date"
                        value={line.expiryDate}
                        onChange={(event) =>
                          patch(line.id, {
                            expiryDate: event.target.value,
                          })
                        }
                        required
                      />
                    </label>
                  </div>
                ) : null}

                <div className="line-sum">
                  <span>Thành tiền</span>
                  <Money value={line.quantity * line.unitCost} />
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card className="form-stack">
        <label>
          Thanh toán
          <select
            value={paymentMethod}
            onChange={(event) =>
              setPaymentMethod(
                event.target.value as "CASH" | "BANK_TRANSFER" | "DEBT",
              )
            }
          >
            <option value="CASH">Tiền mặt</option>
            <option
              value="BANK_TRANSFER"
              disabled={!bankPaymentAvailable}
            >
              Chuyển khoản {bankPaymentAvailable ? "" : "— cần mạng"}
            </option>
            <option value="DEBT">Ghi nợ nhà cung cấp</option>
          </select>
        </label>

        {debtNeedsSupplier ? (
          <InlineNotice tone="warning" title="Cần chọn nhà cung cấp">
            Ghi nợ phải gắn với một nhà cung cấp để công nợ có thể đối chiếu.
          </InlineNotice>
        ) : null}
      </Card>

      <div className="sticky-summary">
        <div>
          <span>Tổng nhập</span>
          <Money value={total} className="money-large" />
        </div>

        <button
          className="btn btn-primary btn-lg"
          disabled={
            busy ||
            lines.length === 0 ||
            debtNeedsSupplier ||
            (paymentMethod === "BANK_TRANSFER" && !bankPaymentAvailable)
          }
          onClick={() => void submit()}
        >
          <PackageCheck size={20} />{" "}
          {busy ? "Đang lưu..." : "Hoàn tất nhập hàng"}
        </button>
      </div>
    </div>
  );
}
